/**
 * SPV Connect accounts.
 *
 * An SPV is the legal entity that receives an event's net proceeds, and it is NOT the
 * organizer: the same promoter can stand behind many SPVs, and one production spanning
 * several dates is a single entity across several events.
 *
 * Every field here is per record. There is no default entity name and no fallback
 * account anywhere in this client, because many SPVs run concurrently and each carries
 * its own legal name and its own Stripe account.
 */

import { api, ApiError } from './apiClient';

export type OnboardingStatus = 'not_started' | 'pending' | 'complete' | 'restricted';

export interface SpvProfile {
  id: string;
  legalEntityName: string;
  stripeConnectAccountId: string | null;
  onboardingStatus: OnboardingStatus;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  onboardingCompletedAt: string | null;
  contactEmail: string | null;
  notes: string | null;
  createdAt: string;
  /** Stripe's requirements.disabled_reason. Null unless the account is disabled. */
  stripeDisabledReason: string | null;
  /** What this specific SPV must supply before payouts resume. */
  requirementsDue: string[];
  /** The only question the gate asks. Stripe's payouts_enabled decides it, not the status. */
  canReceiveTransfers: boolean;
  eventCount?: number;
}

export const listSpvs = () => api.get<SpvProfile[]>('/admin/spvs');

/**
 * Add an SPV. Finds the entity if it already exists rather than making a second record,
 * and refuses with a 409 when the name is merely CLOSE to one, unless confirmCreate says
 * the admin has looked at the candidates and meant a different company.
 */
export const createSpv = (input: {
  legalEntityName: string; contactEmail?: string | null; notes?: string | null;
  confirmCreate?: boolean;
}) => api.post<SpvProfile & { created: boolean }>('/admin/spvs', input);

/** Returns a short lived hosted onboarding URL. Regenerated on demand, never stored. */
export const startOnboarding = (spvId: string) =>
  api.post<{ url: string; expiresAt: number; spv: SpvProfile }>(
    `/admin/spvs/${spvId}/onboarding`, { returnBase: window.location.origin });

/** Ask Stripe what it actually thinks, and store the answer. */
export const refreshOnboarding = (spvId: string) =>
  api.post<SpvProfile>(`/admin/spvs/${spvId}/refresh`, {});

/**
 * Attach by legal name, creating the entity if no company by that name exists.
 *
 * `created` distinguishes the two outcomes and must be shown: an admin who sees "created"
 * where they expected an existing company has just caught their own typo, and that is the
 * only defence against a free-text field spawning a duplicate legal entity.
 */
export const attachSpvByName = (
  eventId: string, legalEntityName: string, confirmCreate = false,
) =>
  api.post<{ spv: SpvProfile; created: boolean }>(
    `/admin/events/${eventId}/spv`, { legalEntityName, confirmCreate });

/**
 * The 409 body when a typed name is close to an entity that already exists.
 *
 * Not an error to report and move on from: it is a question. The engine refuses to guess
 * whether "Foo LLC" and "Foo Ltd" are one company, so it hands back what it found and
 * waits to be told. Answering with a candidate's exact name links it; answering with
 * confirmCreate makes the second company deliberately.
 */
export interface SimilarSpvNames {
  error: 'spv_name_similar';
  typed: string;
  candidates: Array<{ id: string; legalEntityName: string }>;
}

export function similarNamesFrom(err: unknown): SimilarSpvNames | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const p = err.payload as Partial<SimilarSpvNames> | undefined;
  return p?.error === 'spv_name_similar' && Array.isArray(p.candidates)
    ? (p as SimilarSpvNames)
    : null;
}

export const retrySpvPayout = (eventId: string) =>
  api.post<{ transferred: boolean; reason?: string; retryable?: boolean }>(
    `/admin/events/${eventId}/spv-payout/retry`, {});

// ── SPV distributions to investors ────────────────────────────────────────────
//
// Voleto does not make these payments. Net proceeds left for the SPV's account at
// settlement and the SPV pays its investors from there, so these record what an admin has
// confirmed already happened somewhere else.

export interface InvestorPayout {
  id: string;
  investmentId: string;
  contractNumber: string;
  investor: { email: string; name: string | null };
  /** What settlement computed is owed. */
  grossPayoutCents: number;
  performanceFeeCents: number;
  netPayoutCents: number;
  /** What the SPV actually sent. Null until confirmed. */
  distributedAmountCents: number | null;
  distributedAt: string | null;
  distributionReference: string | null;
  distributionNote: string | null;
  confirmedBy: string | null;
  confirmedAt: string | null;
  status: string;
  isDistributed: boolean;
}

export const listEventPayouts = (eventId: string) =>
  api.get<InvestorPayout[]>(`/admin/events/${eventId}/payouts`);

export const recordDistribution = (payoutId: string, input: {
  amountCents: number; distributedAt?: string | null;
  reference?: string | null; note?: string | null;
}) => api.post<InvestorPayout>(`/admin/payouts/${payoutId}/distribution`, input);
